<template>
  <div class="sparkline" :style="{ height: height + 'px' }">
    <svg :viewBox="`0 0 ${width} ${height}`" preserveAspectRatio="none" class="sparkline-svg">
      <defs>
        <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" :stop-color="color" stop-opacity="0.35" />
          <stop offset="100%" :stop-color="color" stop-opacity="0" />
        </linearGradient>
      </defs>
      <path v-if="areaPath" :d="areaPath" :fill="`url(#${gradientId})`" />
      <path v-if="linePath" :d="linePath" :stroke="color" fill="none" :stroke-width="strokeWidth" stroke-linejoin="round" stroke-linecap="round" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  points: number[] | { t: string; v: number }[]
  width?: number
  height?: number
  color?: string
  strokeWidth?: number
}>()

const width = computed(() => props.width ?? 120)
const height = computed(() => props.height ?? 32)
const color = computed(() => props.color ?? '#1976D2')
const strokeWidth = computed(() => props.strokeWidth ?? 2)
const gradientId = `spark-${Math.random().toString(36).slice(2, 8)}`

const values = computed<number[]>(() => {
  if (!props.points || props.points.length === 0) return []
  const first = props.points[0] as any
  if (typeof first === 'number') return props.points as number[]
  return (props.points as any[]).map(p => Number(p.v) || 0)
})

function buildPath(vals: number[], w: number, h: number): { line: string; area: string } | null {
  if (!vals.length) return null
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const range = Math.max(1, max - min)
  const n = vals.length
  const stepX = n > 1 ? w / (n - 1) : w

  const pts = vals.map((v, i) => {
    const x = Math.round(i * stepX)
    const y = Math.round(h - ((v - min) / range) * (h - 2)) // margen superior/inferior
    return { x, y }
  })

  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const area = `${line} L ${w} ${h} L 0 ${h} Z`
  return { line, area }
}

const linePath = computed(() => buildPath(values.value, width.value, height.value)?.line || '')
const areaPath = computed(() => buildPath(values.value, width.value, height.value)?.area || '')
</script>

<style scoped>
.sparkline {
  width: 100%;
}
.sparkline-svg {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
